import { Fragment, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  ShieldAlert,
  Trash2,
  Play,
  ChevronDown,
  ChevronRight,
  Archive,
  ArchiveRestore,
  GitBranch,
} from "lucide-react";
import { apiService } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import {
  canCreateThreatModel,
  canDeleteThreatModel,
  canEditThreatModel,
  type ThreatModelFamilyItem,
  type ThreatModelVersion,
} from "../types";
import Pagination from "../components/Pagination";
import ThreatModelVersionDialog from "../components/ThreatModelVersionDialog";
import toast from "react-hot-toast";

const PHASE_COLORS: Record<string, string> = {
  created: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  discovery: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  stride:
    "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  complete: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  running:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  completed:
    "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

const RUNNING_PHASES = new Set(["discovery", "stride"]);

export default function ThreatModelsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [newVersionFor, setNewVersionFor] = useState<{
    anchorId: string;
    familyName: string;
  } | null>(null);
  const [triggerModal, setTriggerModal] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [triggerStage, setTriggerStage] = useState("");
  const [triggerAutonomous, setTriggerAutonomous] = useState(true);

  const filtersActive = !!appliedSearch || !!phaseFilter;

  const { data: familiesData, isLoading } = useQuery(
    [
      "threat-model-families",
      appliedSearch,
      phaseFilter,
      showArchived,
      currentPage,
      itemsPerPage,
    ],
    () =>
      apiService.getThreatModelFamilies({
        search: appliedSearch || undefined,
        phase: phaseFilter || undefined,
        include_archived: showArchived || undefined,
        page: currentPage,
        page_size: itemsPerPage,
      }),
    {
      keepPreviousData: true,
      refetchInterval: (data) => {
        const items: ThreatModelFamilyItem[] = data?.data?.data?.items ?? [];
        return items.some((f) =>
          f.versions.some((v) => RUNNING_PHASES.has(v.phase)),
        )
          ? 5000
          : false;
      },
    },
  );

  const families: ThreatModelFamilyItem[] = useMemo(
    () => familiesData?.data?.data?.items ?? [],
    [familiesData],
  );
  const totalFamilies: number = familiesData?.data?.data?.total ?? 0;
  const totalPages: number = familiesData?.data?.data?.totalPages ?? 0;

  // Filters reveal matching versions instead of hiding them under a collapsed
  // family row; otherwise families start collapsed.
  const isExpanded = (familyId: string) => expanded[familyId] ?? filtersActive;

  const toggleFamily = (familyId: string) =>
    setExpanded((prev) => ({ ...prev, [familyId]: !isExpanded(familyId) }));

  const invalidateLists = () => {
    queryClient.invalidateQueries(["threat-model-families"]);
    queryClient.invalidateQueries(["threat-model-versions"]);
  };

  const deleteMutation = useMutation(
    (id: string) => apiService.deleteThreatModel(id),
    {
      onSuccess: () => {
        invalidateLists();
        toast.success("Threat model version deleted");
        setDeleteConfirmId(null);
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.status === 409
            ? `${err.response.data?.error ?? "This version is in use"}. Archive it instead.`
            : "Failed to delete threat model version",
        );
        setDeleteConfirmId(null);
      },
    },
  );

  const archiveMutation = useMutation(
    ({ id, archive }: { id: string; archive: boolean }) =>
      archive
        ? apiService.archiveThreatModel(id)
        : apiService.unarchiveThreatModel(id),
    {
      onSuccess: (_res, { archive }) => {
        invalidateLists();
        toast.success(archive ? "Version archived" : "Version restored");
      },
      onError: () => {
        toast.error("Failed to update archive state");
      },
    },
  );

  const triggerMutation = useMutation(
    (data: { id: string; stage: string; autonomous: boolean }) =>
      apiService.triggerStage(data.id, {
        stage: data.stage,
        autonomous: data.autonomous,
      }),
    {
      onSuccess: () => {
        invalidateLists();
        toast.success("Stage triggered — agent processing...");
        setTriggerModal(null);
      },
      onError: () => {
        toast.error("Failed to trigger stage. Is NATS running?");
        setTriggerModal(null);
      },
    },
  );

  const handleSearch = () => {
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Threat Models
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage threat modeling sessions and findings
          </p>
        </div>
        {canCreateThreatModel(user) && (
          <Link
            to="/threat-modelling/create"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Create Threat Model
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, description or version..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSearch}
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={phaseFilter}
            onChange={(e) => {
              setPhaseFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-lg border border-gray-300 py-2 pl-3 pr-8 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Phases</option>
            <option value="created">Created</option>
            <option value="discovery">Discovery</option>
            <option value="stride">STRIDE</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => {
              setShowArchived(e.target.checked);
              setCurrentPage(1);
            }}
            className="rounded border-gray-300"
          />
          Show archived
        </label>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      ) : families.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 py-12 dark:border-gray-600">
          <ShieldAlert className="h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No threat models found
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {canCreateThreatModel(user)
              ? "Create your first threat model to get started."
              : "Threat models linked to your products or repositories will appear here."}
          </p>
          {canCreateThreatModel(user) && (
            <Link
              to="/threat-modelling/create"
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Create Threat Model
            </Link>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Name / Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Phase
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Findings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
              {families.map((family) => {
                const open = isExpanded(family.id);
                const anchor = family.versions[0];
                return (
                  <Fragment key={family.id}>
                    <tr
                      className="cursor-pointer bg-gray-50/50 hover:bg-gray-50 dark:bg-gray-800/50 dark:hover:bg-gray-800"
                      onClick={() => toggleFamily(family.id)}
                    >
                      <td className="whitespace-nowrap px-6 py-3" colSpan={5}>
                        <div className="flex items-center gap-2">
                          {open ? (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {family.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {family.versions.length === 1
                              ? "1 version"
                              : `${family.versions.length} versions`}
                            {anchor && ` · latest ${anchor.version}`}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-3 text-right">
                        {canCreateThreatModel(user) && anchor && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setNewVersionFor({
                                anchorId: anchor.id,
                                familyName: family.name,
                              });
                            }}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                          >
                            <GitBranch className="h-3.5 w-3.5" />
                            New version
                          </button>
                        )}
                      </td>
                    </tr>
                    {open &&
                      family.versions.map((tm) => (
                        <VersionRow
                          key={tm.id}
                          tm={tm}
                          canEdit={canEditThreatModel(user, tm.created_by_id)}
                          canDelete={canDeleteThreatModel(user)}
                          onOpen={() => navigate(`/threat-modelling/${tm.id}`)}
                          onTrigger={() =>
                            setTriggerModal({
                              id: tm.id,
                              name: `${tm.name} ${tm.version}`,
                            })
                          }
                          onArchive={(archive) =>
                            archiveMutation.mutate({ id: tm.id, archive })
                          }
                          onDelete={() => setDeleteConfirmId(tm.id)}
                        />
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalFamilies > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalFamilies}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(n) => {
            setItemsPerPage(n);
            setCurrentPage(1);
          }}
        />
      )}

      {newVersionFor && (
        <ThreatModelVersionDialog
          anchorId={newVersionFor.anchorId}
          familyName={newVersionFor.familyName}
          onClose={() => setNewVersionFor(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Delete Threat Model Version
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Are you sure? This action cannot be undone. Versions linked to a
              product or used as a copy source can only be archived.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirmId)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trigger stage modal */}
      {triggerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Trigger Analysis
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {triggerModal.name}
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Starting Stage
                </label>
                <select
                  value={triggerStage}
                  onChange={(e) => setTriggerStage(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 py-2 pl-3 pr-8 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Auto-detect</option>
                  <option value="discovery">Discovery</option>
                  <option value="stride">STRIDE</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={triggerAutonomous}
                  onChange={(e) => setTriggerAutonomous(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                Autonomous (auto-advance to next stages)
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setTriggerModal(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  triggerMutation.mutate({
                    id: triggerModal.id,
                    stage: triggerStage,
                    autonomous: triggerAutonomous,
                  })
                }
                disabled={triggerMutation.isLoading}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {triggerMutation.isLoading ? "Triggering..." : "Trigger"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface VersionRowProps {
  tm: ThreatModelVersion;
  canEdit: boolean;
  canDelete: boolean;
  onOpen: () => void;
  onTrigger: () => void;
  onArchive: (archive: boolean) => void;
  onDelete: () => void;
}

function VersionRow({
  tm,
  canEdit,
  canDelete,
  onOpen,
  onTrigger,
  onArchive,
  onDelete,
}: VersionRowProps) {
  const archived = !!tm.archived_at;
  return (
    <tr
      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${archived ? "opacity-60" : ""}`}
      onClick={onOpen}
    >
      <td className="whitespace-nowrap py-3 pl-14 pr-6">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
            {tm.version}
          </span>
          {archived && (
            <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              Archived
            </span>
          )}
        </div>
        {tm.source_version && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Based on {tm.source_version.version}
          </div>
        )}
        {tm.version_summary && (
          <div className="max-w-xs truncate text-xs text-gray-500 dark:text-gray-400">
            {tm.version_summary}
          </div>
        )}
      </td>
      <td className="whitespace-nowrap px-6 py-3">
        <span
          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${PHASE_COLORS[tm.phase] || PHASE_COLORS.created}`}
        >
          {tm.phase.replace(/_/g, " ")}
        </span>
        {tm.status && tm.status !== "pending" && (
          <span
            className={`ml-1.5 inline-flex rounded-full px-2 py-1 text-xs font-medium ${STATUS_COLORS[tm.status] || STATUS_COLORS.pending}`}
          >
            {tm.status}
          </span>
        )}
        {tm.results_inherited && (
          <span className="ml-1.5 inline-flex rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            {tm.inherited_from
              ? `Inherited from ${tm.inherited_from.version}`
              : "Inherited results"}
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-6 py-3">
        {tm.findings_summary?.bySeverity ? (
          <div className="flex items-center gap-1.5">
            {tm.findings_summary.bySeverity.Critical > 0 && (
              <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-red-100 px-1.5 text-xs font-bold text-red-800 dark:bg-red-900/30 dark:text-red-300">
                {tm.findings_summary.bySeverity.Critical}
              </span>
            )}
            {tm.findings_summary.bySeverity.High > 0 && (
              <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-orange-100 px-1.5 text-xs font-bold text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                {tm.findings_summary.bySeverity.High}
              </span>
            )}
            {tm.findings_summary.bySeverity.Medium > 0 && (
              <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-amber-100 px-1.5 text-xs font-bold text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                {tm.findings_summary.bySeverity.Medium}
              </span>
            )}
            {tm.findings_summary.bySeverity.Low > 0 && (
              <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-gray-100 px-1.5 text-xs font-bold text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                {tm.findings_summary.bySeverity.Low}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-500 dark:text-gray-400">
        {tm.created_by_name || "—"}
      </td>
      <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-500 dark:text-gray-400">
        {new Date(tm.updated_at).toLocaleDateString()}
      </td>
      <td className="whitespace-nowrap px-6 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          {canEdit && !archived && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onTrigger();
              }}
              title="Trigger analysis"
              className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
            >
              <Play className="h-4 w-4" />
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onArchive(!archived);
              }}
              title={archived ? "Restore version" : "Archive version"}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {archived ? (
                <ArchiveRestore className="h-4 w-4" />
              ) : (
                <Archive className="h-4 w-4" />
              )}
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              title="Delete version"
              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
